import "./Profile.scss";
import { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  useMediaQuery,
  IconButton,
  InputAdornment,
  Snackbar,
  Alert,
} from "@mui/material";
import BlueButton from "../../components/BlueButton/BlueButton";
import AddPhoto from "./../../assets/add-photo.png";
import Loader from "../../components/Loader";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import api from "../../utils/api";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateUserProfile } from "../../redux/authSlice";

const Profile = () => {
  const { userData } = useSelector((state) => state.auth);
  const user = userData?.user || userData;
  const dispatch = useDispatch();
  const [currentView, setCurrentView] = useState("main");
  const [countries, setCountries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    text: "",
    severity: "success",
  });
  const isMobile = useMediaQuery("(max-width:767px)");

  // Form state
  const [formData, setFormData] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    email: user?.email || "",
    country_id: user?.country_id || "",
    role: "admin",
    currentPassword: "",
    password: "",
    password_confirmation: "",
  });

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Image handling
  const [image, setImage] = useState(user?.image || null);
  const [removeImage, setRemoveImage] = useState(false);

  // Fetch countries on component mount
  useEffect(() => {
    const getCountries = async () => {
      try {
        const res = await api.get("/countries");
        setCountries(res.data.data);
      } catch (error) {
        console.error("Failed to fetch countries:", error);
      }
    };

    getCountries();

    // Initialize form data with user data
    if (user) {
      setFormData({
        ...formData,
        name: user.name || "",
        phone: user.phone || "",
        email: user.email || "",
        country_id: user.country_id || "",
      });
    }
  }, [user]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle image change
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
      setRemoveImage(false);
    }
  };

  // Handle removing image
  const handleRemoveImage = () => {
    setImage(null);
    setRemoveImage(true);
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    if (field === "current") {
      setShowCurrentPassword(!showCurrentPassword);
    } else if (field === "new") {
      setShowNewPassword(!showNewPassword);
    } else if (field === "confirm") {
      setShowConfirmPassword(!showConfirmPassword);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      let response;

      if (currentView === "main") {
        response = await handleProfileUpdate();
      } else {
        response = await handlePasswordChange();
      }

      toast.success("تم حفظ البيانات بنجاح");
      handleSuccessfulUpdate(response);
    } catch (error) {
      handleSubmitError(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle profile data update
  const handleProfileUpdate = async () => {
    const formDataToSend = new FormData();

    // Add profile fields
    formDataToSend.append("name", formData.name);
    formDataToSend.append("phone", formData.phone);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("country_id", formData.country_id);
    formDataToSend.append("role", "admin");

    // Handle image operations
    if (image instanceof File) {
      formDataToSend.append("image", image);
    }
    if (removeImage) {
      formDataToSend.append("remove_image", "1");
    }

    return await api.put(`/users/${user.id}`, formDataToSend);
  };

  // Handle password change
  const handlePasswordChange = async () => {
    const passwordData = {
      oldPassword: formData.currentPassword,
      newPassword: formData.password,
      newPassword_confirmation: formData.password_confirmation,
    };

    return await api.post("/change_password", passwordData);
  };

  // Handle successful update operations
  const handleSuccessfulUpdate = (response) => {
    if (currentView === "main" && response?.data) {
      // Update Redux store with new user data
      dispatch(updateUserProfile(response.data.data));
    }

    if (currentView === "password") {
      // Reset password fields
      setFormData((prev) => ({
        ...prev,
        currentPassword: "",
        password: "",
        password_confirmation: "",
      }));
    }
  };

  // Handle submission errors
  const handleSubmitError = (error) => {
    console.error("Error updating profile:", error);

    const errorMessage =
      error.response?.data?.message || "حدث خطأ أثناء حفظ البيانات";
    toast.error(errorMessage);
  };

  // Handle closing the snackbar
  const handleCloseMessage = () => {
    setMessage({ ...message, open: false });
  };

  if (isLoading) return <Loader />;

  return (
    <div className="profile">
      <div className="container">
        <div className="tabs">
          <BlueButton
            onClick={() => setCurrentView("main")}
            text="البيانات الاساسية"
            size={isMobile ? "full" : "large"}
            variant={currentView === "main" ? "fill" : "outlined"}
          />
          <BlueButton
            onClick={() => setCurrentView("password")}
            text="تغيير كلمة المرور"
            size={isMobile ? "full" : "large"}
            variant={currentView === "password" ? "fill" : "outlined"}
          />
        </div>
        <form onSubmit={handleSubmit}>
          {currentView === "main" ? (
            <>
              <div className="form-img">
                <label htmlFor="image" className="img-control">
                  <img
                    src={
                      image instanceof File
                        ? URL.createObjectURL(image)
                        : image || AddPhoto
                    }
                    alt=""
                  />
                  <span>اضافة صورة شخصية</span>
                </label>
                <input
                  type="file"
                  id="image"
                  onChange={handleImageChange}
                  accept="image/*"
                />
                {(image || (image === "existing" && !removeImage)) && (
                  <div className="remove-image" onClick={handleRemoveImage}>
                    <DeleteIcon className="icon" />
                  </div>
                )}
              </div>
              <div className="control-group">
                <FormControl sx={{ flex: 1 }} variant="outlined" required>
                  <InputLabel
                    htmlFor="name"
                    shrink
                    sx={{
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.8)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    الاسم
                  </InputLabel>
                  <OutlinedInput
                    sx={{ borderRadius: "8px" }}
                    id="name"
                    type="text"
                    notched
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1 }} variant="outlined" required>
                  <InputLabel
                    htmlFor="phone"
                    shrink
                    sx={{
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.8)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    رقم الموبايل
                  </InputLabel>
                  <OutlinedInput
                    sx={{ borderRadius: "8px" }}
                    id="phone"
                    type="text"
                    notched
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </FormControl>
              </div>
              <div className="control-group">
                <FormControl sx={{ flex: 1 }} variant="outlined" required>
                  <InputLabel
                    htmlFor="email"
                    shrink
                    sx={{
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.8)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    الايميل
                  </InputLabel>
                  <OutlinedInput
                    sx={{ borderRadius: "8px" }}
                    id="email"
                    type="email"
                    notched
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </FormControl>

                <FormControl sx={{ flex: 1 }} variant="outlined" required>
                  <InputLabel
                    htmlFor="country_id"
                    shrink
                    sx={{
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.75)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    نطاق العمل
                  </InputLabel>
                  <Select
                    labelId="country-select-label"
                    id="country_id"
                    displayEmpty={true}
                    sx={{ borderRadius: "8px" }}
                    value={formData.country_id}
                    name="country_id"
                    onChange={handleChange}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </>
          ) : (
            <>
              <div className="control-group">
                <FormControl sx={{ flex: 1 }} variant="outlined" required>
                  <InputLabel
                    htmlFor="currentPassword"
                    shrink
                    sx={{
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.8)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    كلمة المرور الحالية
                  </InputLabel>
                  <OutlinedInput
                    sx={{ borderRadius: "8px" }}
                    id="currentPassword"
                    type={showCurrentPassword ? "text" : "password"}
                    notched
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => togglePasswordVisibility("current")}
                          edge="end"
                        >
                          {showCurrentPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </div>
              <div className="control-group">
                <FormControl sx={{ flex: 1 }} variant="outlined" required>
                  <InputLabel
                    htmlFor="password"
                    shrink
                    sx={{
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.8)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    كلمة المرور الجديدة
                  </InputLabel>
                  <OutlinedInput
                    sx={{ borderRadius: "8px" }}
                    id="password"
                    type={showNewPassword ? "text" : "password"}
                    notched
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => togglePasswordVisibility("new")}
                          edge="end"
                        >
                          {showNewPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>

                <FormControl sx={{ flex: 1 }} variant="outlined" required>
                  <InputLabel
                    htmlFor="password_confirmation"
                    shrink
                    sx={{
                      background: "white",
                      transformOrigin: "right top",
                      transform: "translate(14px, -9px) scale(0.8)",
                      right: 24,
                      left: "auto",
                      paddingLeft: "4px",
                      paddingRight: "4px",
                    }}
                  >
                    تاكيد كلمة المرور
                  </InputLabel>
                  <OutlinedInput
                    sx={{ borderRadius: "8px" }}
                    id="password_confirmation"
                    type={showConfirmPassword ? "text" : "password"}
                    notched
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => togglePasswordVisibility("confirm")}
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </div>
            </>
          )}
          <div className="action-buttons">
            <BlueButton
              type="submit"
              text={isSaving ? "جاري الحفظ..." : "حفظ التغييرات"}
              size="large"
              variant="fill"
              disabled={isSaving}
            />
          </div>
        </form>
      </div>

      {/* Success/Error Message */}
      <Snackbar
        open={message.open}
        autoHideDuration={6000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseMessage} severity={message.severity}>
          {message.text}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Profile;
